import type { DoctorSubstituteDocumentType } from '@omecare/utils';
import { useMutation } from '@tanstack/react-query';
import {
  closePendingDocumentTab,
  navigatePendingDocumentTab,
} from '@/lib/pending-document-tab';
import http from '@/utils/http.service';

export type OpenDoctorSubstituteDocumentParams = {
  doctorSubstituteId: string;
  type: DoctorSubstituteDocumentType;
  /**
   * Tab opened synchronously in the click handler (see
   * `openPendingDocumentTab` in `@/lib/pending-document-tab`); `null` when
   * the browser blocked it.
   */
  tab: Window | null;
};

const getDoctorSubstituteDocumentUrl = (
  params: OpenDoctorSubstituteDocumentParams,
): Promise<string> => {
  return http.doctorSubstitutes
    .getDocumentUrl({ id: params.doctorSubstituteId, type: params.type })
    .then(({ data }) => data.url);
};

/**
 * Fetches a short-lived presigned URL of a substitute supporting document on
 * demand and shows it in the tab the caller opened synchronously. Imperative
 * by design: the URL expires in five minutes, so it is never cached. Failures
 * are toasted by the global `MutationCache.onError`.
 *
 * @returns `open` to trigger the fetch, and whether one is in flight
 */
export const useOpenDoctorSubstituteDocument = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: getDoctorSubstituteDocumentUrl,
    onSuccess(url, params) {
      navigatePendingDocumentTab({ tab: params.tab, url });
    },
    onError(_error, params) {
      closePendingDocumentTab(params.tab);
    },
  });

  return { open: mutate, isOpening: isPending };
};
